import { z } from 'zod';

/**
 * Subscription tiers sold on mandistry.com (website: `src/data/plans.ts`, keep in sync). Amounts live
 * only in Stripe: each tier has one price per interval with lookup key `<plan>_<interval>`.
 * Enterprise is never sold through Checkout; a platform admin assigns it with limit overrides.
 */
export const PLAN_IDS = ['starter', 'team', 'business', 'enterprise'] as const;
export const PlanId = z.enum(PLAN_IDS);
export type PlanId = z.infer<typeof PlanId>;
export const SELF_SERVE_PLANS = ['starter', 'team', 'business'] as const satisfies readonly PlanId[];
export const SelfServePlan = z.enum(SELF_SERVE_PLANS);
export type SelfServePlan = z.infer<typeof SelfServePlan>;

/** `null` = unlimited. */
export const PlanLimits = z.object({
  companies: z.number().int().positive().nullable(),
  agents: z.number().int().positive().nullable(),
  seats: z.number().int().positive().nullable(),
  runsPerMonth: z.number().int().positive().nullable(),
  sandboxHoursPerMonth: z.number().int().positive().nullable(),
  auditRetentionDays: z.number().int().positive().nullable(),
});
export type PlanLimits = z.infer<typeof PlanLimits>;
export const PlanLimitsOverride = PlanLimits.partial();
export type PlanLimitsOverride = z.infer<typeof PlanLimitsOverride>;

export const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
  starter: {
    companies: 1,
    agents: 5,
    seats: 2,
    runsPerMonth: 2_000,
    sandboxHoursPerMonth: 20,
    auditRetentionDays: 30,
  },
  team: {
    companies: 3,
    agents: 25,
    seats: 10,
    runsPerMonth: 10_000,
    sandboxHoursPerMonth: 100,
    auditRetentionDays: 365,
  },
  business: {
    companies: 10,
    agents: null,
    seats: 25,
    runsPerMonth: 50_000,
    sandboxHoursPerMonth: 400,
    auditRetentionDays: 3 * 365,
  },
  // Placeholder until a platform admin sets overrides for the customer.
  enterprise: {
    companies: 10,
    agents: null,
    seats: 25,
    runsPerMonth: 50_000,
    sandboxHoursPerMonth: 400,
    auditRetentionDays: 3 * 365,
  },
};

/** Capabilities sold from Team upward (website "Everything in Starter, plus"). */
export const PLAN_FEATURES = [
  /** `manage_company`: agents that hire and manage agents. */
  'agentsHireAgents',
  /** Automatic git workspaces (checkout, tests, pull requests). */
  'gitWorkspaces',
  /** Recorded browser flows. */
  'browserFlows',
  /** Telegram, Slack and Discord notification channels. */
  'chatChannels',
  /** Personal access tokens and the MCP endpoint. */
  'apiTokens',
  /** Daily run-error analysis. */
  'runAnalysis',
] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];
export const PLANS_WITH_FEATURES: readonly PlanId[] = ['team', 'business', 'enterprise'];
/** Exempt accounts have everything; no plan yet = Starter. */
export const planHasFeatures = (plan: string | null, exempt = false): boolean =>
  exempt || (PLANS_WITH_FEATURES as readonly string[]).includes(plan ?? '');

/** Limits for a billing account: exempt = unlimited (`null`); no plan yet = the smallest tier. */
export function effectiveLimits(
  plan: PlanId | null,
  override?: PlanLimitsOverride | null,
  exempt = false,
): PlanLimits | null {
  if (exempt) return null;
  return { ...PLAN_LIMITS[plan ?? 'starter'], ...(override ?? {}) };
}

export const BillingInterval = z.enum(['month', 'year']);
export type BillingInterval = z.infer<typeof BillingInterval>;

export const priceLookupKey = (plan: SelfServePlan, interval: BillingInterval) => `${plan}_${interval}`;

export function parsePriceLookupKey(
  key: string | null | undefined,
): { plan: SelfServePlan; interval: BillingInterval } | null {
  const [plan, interval, ...rest] = (key ?? '').split('_');
  const p = SelfServePlan.safeParse(plan);
  const i = BillingInterval.safeParse(interval);
  return p.success && i.success && rest.length === 0 ? { plan: p.data, interval: i.data } : null;
}

/** `runs.scheduled_reason` of a new run that waits for the next quota period (monthly runs used up). */
export const PLAN_LIMIT_REASON = 'plan_limit';

/** VAT handling: `kor` = small-business scheme (no VAT charged), `standard` = Stripe Tax. */
export const TaxMode = z.enum(['kor', 'standard']);
export type TaxMode = z.infer<typeof TaxMode>;
